import { useEffect, useRef } from 'react';
import { appScreenshots, socialFeatures } from '@/mocks/homeData';

const featureIcons: Record<string, string> = {
  'Sharing': 'ri-share-forward-line',
  'Chatting': 'ri-chat-smile-2-line',
  'Making plans': 'ri-calendar-event-line',
  'Gifting': 'ri-gift-line',
  'Meeting up': 'ri-map-pin-line',
  'Creating moments': 'ri-heart-pulse-line',
};

export default function SocialExperienceSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.querySelectorAll('.social-animate').forEach((child, i) => {
              (child as HTMLElement).style.animationDelay = `${i * 0.1}s`;
              child.classList.add('animate-fade-in-up');
            });
            obs.unobserve(el);
          }
        });
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} className="relative w-full py-24 md:py-32 bg-background-100">
      {/* Yellow accent line top */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-500/20 to-transparent" />

      <div className="w-full px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left — Phone + text */}
          <div>
            <p className="social-animate opacity-0 text-primary-500 text-xs font-bold tracking-[0.3em] uppercase mb-4">
              Social Experience
            </p>
            <h2 className="social-animate opacity-0 text-3xl sm:text-4xl md:text-5xl font-black text-foreground-50 tracking-tight leading-tight mb-6">
              A drink is just<br />
              <span className="text-primary-500">the beginning.</span>
            </h2>
            <p className="social-animate opacity-0 text-foreground-500 text-base leading-relaxed mb-8 max-w-md">
              MARKER is not just about purchasing a drink. It is about sharing, chatting, making plans, gifting, meeting up, and creating moments that last.
            </p>

            {/* Feature grid */}
            <div className="grid grid-cols-2 gap-3">
              {socialFeatures.map((f) => {
                const icon = featureIcons[f.label] || 'ri-check-line';
                return (
                  <div
                    key={f.label}
                    className="social-animate opacity-0 group flex items-start gap-3 p-4 rounded-xl bg-background-950/60 border border-background-200/30 hover:border-primary-500/30 hover:bg-background-950/80 transition-all duration-300 cursor-default"
                  >
                    <div className="w-9 h-9 rounded-lg bg-primary-500/15 group-hover:bg-primary-500/25 flex items-center justify-center shrink-0 mt-0.5 transition-colors duration-300">
                      <i className={`${icon} text-primary-500 text-base`} />
                    </div>
                    <div>
                      <p className="text-foreground-50 text-sm font-semibold">{f.label}</p>
                      <p className="text-foreground-600 text-xs">{f.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right — Chat phone mockup */}
          <div className="social-animate opacity-0 flex justify-center">
            <div className="phone-frame-dark w-[280px]">
              <div className="phone-notch-dark" />
              <img
                src={appScreenshots.chat}
                alt="MARKER Chat"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}